import { api } from '@/config/api';

export type ProjectStatus = 'planning' | 'active' | 'on_hold' | 'completed' | 'cancelled';
export type ProjectBillingType = 'fixed' | 'hourly' | 'non_billable';

export interface ProjectMilestone {
  milestoneUid: string;
  name: string;
  dueDate?: string;
  done: boolean;
  doneAt?: string;
}

export interface ProjectSummary {
  loggedMinutes: number;
  billableMinutes: number;
  billableAmount: number;
  budgetUsedPct: number | null;
  overdue: boolean;
  milestones: { total: number; done: number };
}

export interface Project {
  _id: string;
  projectUid: string;
  companyUid: string;
  name: string;
  description?: string;
  clientContactUid?: string;
  status: ProjectStatus;
  startDate?: string;
  dueDate?: string;
  billingType: ProjectBillingType;
  budgetAmount?: number;
  hourlyRate?: number;
  currency: string;
  budgetHours?: number;
  ownerUserUid?: string;
  memberUserUids: string[];
  milestones: ProjectMilestone[];
  color: string;
  createdAt: string;
  updatedAt: string;
  summary?: ProjectSummary;
}

export interface TimeEntry {
  _id: string;
  entryUid: string;
  companyUid: string;
  projectUid: string;
  userUid: string;
  date: string;
  minutes: number;
  note?: string;
  billable: boolean;
  rate?: number;
  invoiced: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ActiveTimer {
  companyUid: string;
  userUid: string;
  projectUid: string;
  note?: string;
  startedAt: string;
  elapsedSeconds: number;
}

export interface TimesheetRow {
  projectUid: string;
  projectName: string;
  color: string;
  byDay: number[];
  total: number;
}

export interface Timesheet {
  userUid: string;
  weekStart: string;
  weekEnd: string;
  days: string[];
  rows: TimesheetRow[];
  dailyTotals: number[];
  weekTotal: number;
}

export interface ListResponse<T> { items: T[]; total: number; page: number; limit: number; }

export interface CreateProjectPayload {
  name: string;
  description?: string;
  clientContactUid?: string;
  status?: ProjectStatus;
  startDate?: string;
  dueDate?: string;
  billingType?: ProjectBillingType;
  budgetAmount?: number;
  hourlyRate?: number;
  currency?: string;
  budgetHours?: number;
  ownerUserUid?: string;
  memberUserUids?: string[];
  color?: string;
}

export const projectsService = {
  list: async (q: { status?: string; client?: string; owner?: string; q?: string; page?: number; limit?: number } = {}): Promise<ListResponse<Project>> =>
    (await api.get('/slighter_crm/projects', { params: q })).data,
  get: async (projectUid: string): Promise<Project> => (await api.get(`/slighter_crm/projects/${projectUid}`)).data,
  create: async (p: CreateProjectPayload): Promise<Project> => (await api.post('/slighter_crm/projects', p)).data,
  update: async (projectUid: string, p: Partial<CreateProjectPayload>): Promise<Project> =>
    (await api.patch(`/slighter_crm/projects/${projectUid}`, p)).data,
  remove: async (projectUid: string): Promise<void> => { await api.delete(`/slighter_crm/projects/${projectUid}`); },

  addMilestone: async (projectUid: string, p: { name: string; dueDate?: string }): Promise<Project> =>
    (await api.post(`/slighter_crm/projects/${projectUid}/milestones`, p)).data,
  updateMilestone: async (projectUid: string, milestoneUid: string, p: { name?: string; dueDate?: string }): Promise<Project> =>
    (await api.patch(`/slighter_crm/projects/${projectUid}/milestones/${milestoneUid}`, p)).data,
  toggleMilestone: async (projectUid: string, milestoneUid: string): Promise<Project> =>
    (await api.post(`/slighter_crm/projects/${projectUid}/milestones/${milestoneUid}/toggle`)).data,
  removeMilestone: async (projectUid: string, milestoneUid: string): Promise<Project> =>
    (await api.delete(`/slighter_crm/projects/${projectUid}/milestones/${milestoneUid}`)).data,

  listTimeEntries: async (q: { projectUid?: string; userUid?: string; dateFrom?: string; dateTo?: string; page?: number; limit?: number } = {}): Promise<ListResponse<TimeEntry>> =>
    (await api.get('/slighter_crm/projects/time-entries', { params: q })).data,
  createTimeEntry: async (p: { projectUid: string; date: string; minutes: number; note?: string; billable?: boolean; userUid?: string }): Promise<TimeEntry> =>
    (await api.post('/slighter_crm/projects/time-entries', p)).data,
  updateTimeEntry: async (entryUid: string, p: { date?: string; minutes?: number; note?: string; billable?: boolean }): Promise<TimeEntry> =>
    (await api.patch(`/slighter_crm/projects/time-entries/${entryUid}`, p)).data,
  removeTimeEntry: async (entryUid: string): Promise<void> => { await api.delete(`/slighter_crm/projects/time-entries/${entryUid}`); },

  currentTimer: async (): Promise<{ running: boolean; timer: ActiveTimer | null }> =>
    (await api.get('/slighter_crm/projects/timer/current')).data,
  startTimer: async (p: { projectUid: string; note?: string }): Promise<ActiveTimer> =>
    (await api.post('/slighter_crm/projects/timer/start', p)).data,
  stopTimer: async (): Promise<TimeEntry> => (await api.post('/slighter_crm/projects/timer/stop')).data,

  timesheet: async (q: { date?: string; userUid?: string } = {}): Promise<Timesheet> =>
    (await api.get('/slighter_crm/projects/timesheet', { params: q })).data,
};
